import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiFileText, FiMonitor, FiLink, FiInfo, FiChevronRight, FiChevronLeft, FiChevronDown, FiChevronUp, FiActivity, FiArrowLeft, FiPlayCircle, FiRadio } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function StudentLessonView() {
    const { monthId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [lessons, setLessons] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeItem, setActiveItem] = useState(null);
    const [expandedSessions, setExpandedSessions] = useState({});
    const [securitySettings, setSecuritySettings] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isIntelOpen, setIsIntelOpen] = useState(true);
    const [showBackToTop, setShowBackToTop] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        };
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 400);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);


    useEffect(() => {
        const loadPageData = async () => {
            try {
                const [lessonsData, settingsData] = await Promise.all([
                    fetchAPI(`/student/get_lessons.php?month_id=${monthId}&user_id=${currentUser?.id}`),
                    fetchAPI('/admin/get_settings.php')
                ]);

                setLessons(lessonsData.lessons);
                setMetadata(lessonsData.metadata);
                setSecuritySettings(settingsData);

                // Set first content item as active if available
                if (lessonsData.lessons.length > 0) {
                    const firstLesson = lessonsData.lessons[0];
                    if (firstLesson.sub_items && firstLesson.sub_items.length > 0) {
                        setActiveItem({
                            ...firstLesson.sub_items[0],
                            sessionTitle: firstLesson.title,
                            lessonDescription: firstLesson.description
                        });
                        setExpandedSessions({ [firstLesson.id]: true });
                    }
                }
            } catch (err) {
                console.error("Failed to load page data", err);
            } finally {
                setLoading(false);
            }
        };
        loadPageData();
    }, [monthId]);

    const getEmbedUrl = (url) => {
        if (!url) return null;
        const ytRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[2].length === 11) {
            return `https://www.youtube.com/embed/${ytMatch[2]}?rel=0&modestbranding=1&showinfo=0&autoplay=1`;
        }
        const vimeoRegex = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
        const vimeoMatch = url.match(vimeoRegex);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`;
        }
        return url;
    };

    const toggleSession = (id) => {
        setExpandedSessions(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const getContentIcon = (sub) => {
        if (sub.pdf_url) return <FiFileText size={16} />;
        if (sub.zoom_link || sub.title?.toLowerCase().includes('live')) return <FiRadio size={16} className="animate-pulse text-red-500" />;
        return <FiMonitor size={16} />;
    };

    const getItemUrl = (sub) => sub.pdf_url || sub.recording_url || sub.zoom_link;

    const handleItemClick = (sub, sessionTitle, lessonDescription) => {
        const itemWithContext = { ...sub, sessionTitle, lessonDescription: lessonDescription || sub.description };
        setActiveItem(itemWithContext);

        // Auto-open in new tab for mobile/Zoom/Live content
        const url = getItemUrl(sub);
        const isZoomOrLive = sub.zoom_link || sub.title?.toLowerCase().includes('live');

        if (url && (isMobile || isZoomOrLive)) {
            window.open(url, '_blank');
        }
    };



    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading Academic Materials...</p>
        </div>
    );

    // --- MOBILE VIEW (New Redesign) ---
    if (isMobile) {
        return (
            <div className="min-h-screen bg-white font-display">
                {/* Header */}
                <div className="sticky top-0 z-50 bg-white border-b border-gray-100 p-4 pt-10 flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 text-gray-400">
                        <FiArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-gray-900 leading-tight uppercase italic">{metadata?.month_name}</h1>
                        <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">{metadata?.class_name}</p>
                    </div>
                </div>

                <div className="p-4 space-y-8 pb-20">
                    {lessons.map((lesson, idx) => (
                        <div key={lesson.id} className="space-y-6">
                            {/* Session Header Card */}
                            <div className="bg-gray-50 p-6 border-l-4 border-orange-400 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex-1">
                                    <h2 className="text-lg font-black text-gray-700 leading-7">
                                        {lesson.title}
                                    </h2>
                                    {lesson.description && (
                                        <p className="text-xs text-gray-400 mt-2 font-medium leading-relaxed">{lesson.description}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full uppercase tracking-widest">
                                        {lesson.sub_items.length} Materials
                                    </span>
                                </div>
                            </div>

                            {/* Grouped Materials */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pl-1">
                                {/* Slides / PDFs */}
                                {lesson.sub_items.filter(si => si.pdf_url).length > 0 && (
                                    <div className="space-y-4">
                                        <h3 className="text-[13px] font-black text-gray-600 uppercase tracking-wider">Lecture slides</h3>
                                        {lesson.sub_items.filter(si => si.pdf_url).map(sub => (
                                            <div key={sub.id} className="space-y-3">
                                                <div onClick={() => handleItemClick(sub, lesson.title, lesson.description)} className="flex items-start gap-3 sm:gap-4 py-2 group cursor-pointer hover:bg-gray-50/50 rounded-xl transition-colors">
                                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white shrink-0 mt-0.5 shadow-lg shadow-blue-200">
                                                        <FiFileText className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm sm:text-[15px] font-bold text-gray-700 line-clamp-1">{sub.title}</span>
                                                        {sub.description && (
                                                            <p className="text-[10px] sm:text-[11px] md:text-xs text-gray-400 mt-1 font-medium leading-relaxed">{sub.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Recordings / Videos */}
                                {(lesson.recording_url || lesson.sub_items.filter(si => si.recording_url).length > 0) && (
                                    <div className="space-y-4">
                                        <h3 className="text-[13px] font-black text-gray-600 uppercase tracking-wider">Recordings</h3>
                                        {/* Child Recordings */}
                                        {lesson.sub_items.filter(si => si.recording_url).map(sub => (
                                            <div key={sub.id} className="space-y-3">
                                                <div onClick={() => handleItemClick(sub, lesson.title, lesson.description)} className="flex items-start gap-3 sm:gap-4 py-2 group cursor-pointer hover:bg-gray-50/50 rounded-xl transition-colors">
                                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white shrink-0 mt-0.5 shadow-lg shadow-blue-200">
                                                        <FiLink className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm sm:text-[15px] font-bold text-gray-700 line-clamp-1">{sub.title}</span>
                                                        {sub.description && (
                                                            <p className="text-[10px] sm:text-[11px] md:text-xs text-gray-400 mt-1 font-medium leading-relaxed">{sub.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Master Recording */}
                                        {lesson.recording_url && (
                                            <div className="space-y-3">
                                                <div onClick={() => handleItemClick({ title: 'Full Session Recording', recording_url: lesson.recording_url, description: 'Complete recording of the live session.' }, lesson.title, lesson.description)} className="flex items-start gap-3 sm:gap-4 py-2 group cursor-pointer hover:bg-orange-50/50 rounded-xl transition-colors">
                                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-400 rounded-lg flex items-center justify-center text-white shrink-0 mt-0.5 shadow-lg shadow-orange-100">
                                                        <FiPlayCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm sm:text-[15px] font-bold text-gray-700 line-clamp-1">Full Session Video</span>
                                                        <p className="text-[10px] sm:text-[11px] md:text-xs text-gray-400 mt-1 font-medium leading-relaxed">Official archival footage of the classroom session.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Zoom / Live */}
                                {lesson.sub_items.filter(si => si.zoom_link).length > 0 && (
                                    <div className="space-y-4">
                                        <h3 className="text-[13px] font-black text-gray-600 uppercase tracking-wider">Live Sessions</h3>
                                        {lesson.sub_items.filter(si => si.zoom_link).map(sub => (
                                            <div key={sub.id} className="space-y-3">
                                                <div onClick={() => handleItemClick(sub, lesson.title, lesson.description)} className="flex items-start gap-3 sm:gap-4 py-2 group cursor-pointer hover:bg-red-50/50 rounded-xl transition-colors">
                                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-500 rounded-lg flex items-center justify-center text-white shrink-0 mt-0.5 shadow-lg shadow-red-100">
                                                        <FiRadio className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm sm:text-[15px] font-bold text-gray-700 line-clamp-1">{sub.title}</span>
                                                        {sub.description && (
                                                            <p className="text-[10px] sm:text-[11px] md:text-xs text-gray-400 mt-1 font-medium leading-relaxed">{sub.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <hr className="border-gray-100" />
                        </div>
                    ))}
                </div>

                {/* Floating Back to Top Button */}
                <AnimatePresence>
                    {showBackToTop && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.5, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.5, y: 20 }}
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="fixed bottom-6 right-4 w-12 h-12 bg-orange-500 text-white rounded-2xl shadow-[0_8px_30px_rgb(249,115,22,0.4)] flex items-center justify-center hover:scale-110 transition-transform active:scale-95 z-[60] border-2 border-white"
                        >
                            <FiChevronUp size={24} />
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // --- DESKTOP VIEW (Unchanged) ---
    return (
        <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
            {/* SyZyGy Style Header */}
            <div className="h-16 md:h-20 bg-blue-600 flex items-center justify-between px-6 shadow-xl z-20">
                <div className="flex items-center gap-4">
                    <button
                        className="p-2 hover:bg-white/10 text-white rounded-lg transition-all"
                    >
                        <FiArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-white text-sm md:text-lg font-black uppercase tracking-tight italic">
                            {metadata?.month_name} <span className="text-blue-200">CURRICULUM</span>
                        </h1>
                        <p className="text-blue-100 text-[8px] md:text-[10px] font-bold uppercase tracking-widest opacity-80">
                            {metadata?.class_name} • Grade {metadata?.grade}
                        </p>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-3">
                    <div className="h-10 w-px bg-white/20"></div>
                    <div className="text-right">
                        <p className="text-white text-[10px] font-black uppercase tracking-widest leading-none mb-1">Standard View</p>
                        <p className="text-blue-200 text-[8px] font-bold uppercase tracking-widest leading-none">Interactive LMS Mode</p>
                    </div>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden relative">
                {/* Left Sidebar: Lessons Tree */}
                <div className="w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col shadow-inner z-10 overflow-y-auto">
                    <div className="p-4 md:p-6 space-y-4">
                        <div className="w-full py-3 bg-gray-100/50 text-gray-700 text-[11px] font-black rounded-lg border border-gray-200/50 flex items-center justify-center gap-2 uppercase tracking-widest font-display">
                            <FiActivity size={14} /> Academic Roadmap
                        </div>

                        <div className="space-y-4">
                            {lessons.map((lesson, lIdx) => (
                                <div key={lesson.id} className="space-y-2">
                                    <button
                                        onClick={() => toggleSession(lesson.id)}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-all font-display ${expandedSessions[lesson.id] ? 'bg-blue-50/50 text-blue-700' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >

                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="text-[10px] font-black opacity-20">S{String(lIdx + 1).padStart(2, '0')}</div>
                                            <h3 className="text-[11px] font-black uppercase tracking-wide truncate">{lesson.title}</h3>
                                        </div>
                                        {expandedSessions[lesson.id] ? <FiChevronDown /> : <FiChevronRight />}
                                    </button>

                                    <AnimatePresence>
                                        {expandedSessions[lesson.id] && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden pl-4 border-l-2 border-gray-100 ml-4 space-y-1"
                                            >
                                                {lesson.sub_items.map((sub, sIdx) => (
                                                    <button
                                                        key={sub.id || sIdx}
                                                        onClick={() => handleItemClick(sub, lesson.title, lesson.description)}
                                                        className={`w-full group text-left p-2.5 rounded-lg transition-all flex items-center gap-3 font-display ${activeItem?.id === sub.id || (activeItem?.title === sub.title && activeItem?.sessionTitle === lesson.title)
                                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                                            : 'hover:bg-gray-50 text-gray-600'
                                                            }`}
                                                    >
                                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${activeItem?.id === sub.id || (activeItem?.title === sub.title && activeItem?.sessionTitle === lesson.title) ? 'bg-white/20' : 'bg-gray-100'
                                                            }`}>
                                                            {getContentIcon(sub)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] font-black uppercase tracking-tight truncate">{sub.title}</p>
                                                            <p className={`text-[7px] font-bold uppercase tracking-widest ${activeItem?.id === sub.id || (activeItem?.title === sub.title && activeItem?.sessionTitle === lesson.title) ? 'text-blue-100' : 'text-gray-400'
                                                                }`}>
                                                                {sub.pdf_url ? 'PDF Document' : 'Media Payload'}
                                                            </p>
                                                        </div>
                                                    </button>
                                                ))}

                                                {lesson.recording_url && (
                                                    <button
                                                        onClick={() => handleItemClick({
                                                            title: 'Full Session Recording',
                                                            recording_url: lesson.recording_url,
                                                        }, lesson.title, lesson.description)}
                                                        className="w-full group text-left p-2.5 rounded-lg transition-all flex items-center gap-3 hover:bg-gray-50 text-gray-600 font-display"
                                                    >

                                                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gray-100">
                                                            <FiPlayCircle size={16} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] font-black uppercase tracking-tight truncate">Master Recording</p>
                                                            <p className="text-[7px] font-bold uppercase tracking-widest text-gray-400">Class Video</p>
                                                        </div>
                                                    </button>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: Main Content Player */}
                <div className="flex-1 bg-gray-900 flex flex-col relative">
                    {activeItem && (
                        <div className="h-20 bg-gray-800 border-b border-white/5 flex items-center justify-between px-6 shrink-0">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">{activeItem.sessionTitle}</span>
                                    <div className="w-1 h-1 rounded-full bg-white/20"></div>
                                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{activeItem.id ? 'Item #' + activeItem.id : 'Live Content'}</span>
                                </div>
                                <h3 className="text-white text-xs md:text-sm font-black uppercase tracking-tighter truncate">{activeItem.title}</h3>
                            </div>

                            <div className="flex items-center gap-4">
                                {activeItem && (
                                    <button
                                        onClick={() => window.open(getItemUrl(activeItem), '_blank')}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20"
                                    >
                                        <FiLink size={14} /> Open Source Link
                                    </button>
                                )}

                                <div className="hidden md:flex flex-col items-end">
                                    <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                        <span className="text-[10px] font-black text-white italic uppercase">Protocol Active</span>
                                    </div>
                                    <span className="text-[8px] text-gray-500 font-bold uppercase tracking-widest">Secure Handshake: OK</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeItem ? (
                        <div className="flex-1 flex flex-col xl:flex-row min-h-0 bg-black">
                            {/* Primary Media Zone */}
                            <div className="flex-1 flex flex-col min-h-0 relative">
                                {activeItem.pdf_url ? (
                                    <div className="flex-1 relative">
                                        <iframe
                                            src={activeItem.pdf_url.includes('drive.google.com') ? activeItem.pdf_url.replace(/\/view.*$/, '/preview') : activeItem.pdf_url}
                                            className="w-full h-full border-none"
                                            title={activeItem.title}
                                        ></iframe>
                                    </div>
                                ) : (
                                    <div className="flex-1 relative group bg-black">
                                        <iframe
                                            src={getEmbedUrl(getItemUrl(activeItem))}
                                            className="w-full h-full border-none"
                                            allowFullScreen
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            title={activeItem.title}
                                        ></iframe>

                                        {/* Watermark Overlay */}
                                        {securitySettings?.screen_recording_watermark === '1' && (
                                            <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden opacity-10">
                                                <span className="absolute text-[12px] text-white/20 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap" style={{ top: '20%', left: '10%' }}>
                                                    MANOJ MATHS • SECURE MEDIA
                                                </span>
                                                <span className="absolute text-[12px] text-white/20 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap" style={{ bottom: '25%', right: '15%' }}>
                                                    MANOJ MATHS • SECURE MEDIA
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Description Section - Collapsible Sidebar */}
                            {(activeItem.description || activeItem.lessonDescription) && (
                                <div
                                    className={`bg-gray-800 flex flex-col border-t xl:border-t-0 xl:border-l border-white/5 shrink-0 transition-all duration-500 relative ${isIntelOpen ? 'xl:w-80 2xl:w-96' : 'xl:w-12'
                                        }`}
                                >
                                    {/* Toggle Toggle Button */}
                                    <button
                                        onClick={() => setIsIntelOpen(!isIntelOpen)}
                                        className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-blue-600 text-white rounded-full hidden xl:flex items-center justify-center shadow-lg z-30 hover:bg-blue-700 transition-all border-4 border-gray-900"
                                    >
                                        {isIntelOpen ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />}
                                    </button>

                                    <div className={`p-4 md:p-6 flex flex-col h-full transition-opacity duration-300 ${isIntelOpen ? 'opacity-100' : 'opacity-0 xl:hidden'}`}>
                                        <div className="flex items-center gap-2 mb-4 shrink-0">
                                            <FiInfo size={14} className="text-blue-400" />
                                            <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">Material Intel</h4>
                                        </div>
                                        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-6">
                                            {activeItem.description && (
                                                <div className="text-xs text-gray-300 font-medium leading-relaxed whitespace-pre-wrap">
                                                    {activeItem.description}
                                                </div>
                                            )}
                                            {activeItem.lessonDescription && activeItem.lessonDescription !== activeItem.description && (
                                                <div className="pt-4 border-t border-white/5">
                                                    <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest mb-2 italic">Session Context:</p>
                                                    <div className="text-[11px] text-gray-400 font-medium leading-relaxed whitespace-pre-wrap">
                                                        {activeItem.lessonDescription}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Small place indicator when closed */}
                                    {!isIntelOpen && (
                                        <div className="hidden xl:flex flex-col items-center pt-8 gap-6 text-gray-500">
                                            <div className="rotate-90 whitespace-nowrap text-[8px] font-black uppercase tracking-widest opacity-30">
                                                Material Information Panel
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (

                        <div className="w-full h-full flex flex-col items-center justify-center text-center p-12">
                            <div className="w-24 h-24 bg-white/5 rounded-xl flex items-center justify-center mb-6 text-white/10 animate-pulse">
                                <FiActivity size={48} />
                            </div>
                            <h2 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2">Select a lesson to begin</h2>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-widest max-w-md mx-auto">
                                Navigate through the monthly curriculum using the sidebar to the left.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
